/**
 * EoT Claim Builder — Schedule Health.
 *
 * Programmes come in from the platform (macro, micro, saved versions) or from a
 * file (P6 XER, MS Project XML/MPP, spreadsheet, PDF). Each one is scheduled by
 * the deterministic CPM and assessed against the DCMA 14-point checks.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Loader2, Upload, Download, Activity, ShieldCheck, ShieldAlert, ChevronDown, ChevronRight, Trash2,
} from 'lucide-react';

const ROLES = ['BASELINE', 'UPDATE', 'AS_BUILT', 'REVISED', 'REFERENCE'];
const GATE = {
  PASS: { bg: '#DCFCE7', color: '#166534' },
  CAUTION: { bg: '#FEF3C7', color: '#92400E' },
  FAIL: { bg: '#FEE2E2', color: '#991B1B' },
  INFO: { bg: '#E2E8F0', color: '#334155' },
};

const Gate = ({ value, testId }) => (
  <span data-testid={testId} style={{ background: (GATE[value] || GATE.INFO).bg, color: (GATE[value] || GATE.INFO).color }}
        className="inline-block px-2 py-0.5 rounded text-[9px] font-medium uppercase tracking-wider">
    {value || 'not checked'}
  </span>
);

export default function ScheduleHealthTab({ claimId }) {
  const [programmes, setProgrammes] = useState([]);
  const [sources, setSources] = useState([]);
  const [recommendation, setRecommendation] = useState(null);
  const [gate, setGate] = useState('');
  const [ack, setAck] = useState({ done: false, by: '', note: '' });
  const [pick, setPick] = useState({ key: '', role: 'UPDATE', dataDate: '' });
  const [upRole, setUpRole] = useState('UPDATE');
  const [upDate, setUpDate] = useState('');
  const [busy, setBusy] = useState('');
  const [expanded, setExpanded] = useState('');
  const [ackNote, setAckNote] = useState('');
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    const [p, s] = await Promise.all([
      api.get(`/eot/claims/${claimId}/programmes`),
      api.get(`/eot/claims/${claimId}/programme-sources`),
    ]);
    setProgrammes(p.data.programmes || []);
    setRecommendation(p.data.recommendation);
    setGate(p.data.gate || '');
    setAck({ done: p.data.healthAcknowledged, by: p.data.healthAcknowledgedBy, note: p.data.healthAcknowledgedNote });
    setSources(s.data.sources || []);
  }, [claimId]);

  useEffect(() => { load().catch(() => {}); }, [load]);

  const importPlatform = async () => {
    const src = sources.find(s => s.key === pick.key);
    if (!src) return;
    setBusy('import');
    try {
      await api.post(`/eot/claims/${claimId}/programmes/platform`, {
        scheduleId: src.scheduleId, versionId: src.versionId, discipline: src.discipline || '',
        label: src.discipline ? '' : src.label, role: pick.role,
        dataDate: pick.dataDate || (src.dataDate || '').slice(0, 10),
      });
      toast.success('Programme imported and scheduled');
      setPick({ key: '', role: 'UPDATE', dataDate: '' });
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not import that programme'); }
    finally { setBusy(''); }
  };

  const uploadProgramme = async (file) => {
    if (!file) return;
    setBusy('upload');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const qs = `role=${upRole}&dataDate=${encodeURIComponent(upDate)}&label=${encodeURIComponent(file.name)}`;
      await api.post(`/eot/claims/${claimId}/programmes/upload?${qs}`, fd,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`${file.name} read and scheduled`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not read that programme file'); }
    finally { setBusy(''); if (fileRef.current) fileRef.current.value = ''; }
  };

  const patch = async (id, body) => {
    try { await api.patch(`/eot/claims/${claimId}/programmes/${id}`, body); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
  };

  const runHealth = async (id) => {
    setBusy(`health-${id}`);
    try {
      const { data } = await api.post(`/eot/claims/${claimId}/programmes/${id}/health`);
      toast[data.health.gate === 'FAIL' ? 'warning' : 'success'](
        `${data.health.passed} of 14 checks passed — ${data.health.gate}`);
      setExpanded(id);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not assess that programme'); }
    finally { setBusy(''); }
  };

  const remove = async (id) => {
    try { await api.delete(`/eot/claims/${claimId}/programmes/${id}`); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not remove'); }
  };

  const acknowledge = async () => {
    setBusy('ack');
    try {
      await api.post(`/eot/claims/${claimId}/health/acknowledge`, { note: ackNote });
      toast.success('Acknowledged and recorded against the claim');
      setAckNote('');
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not record that'); }
    finally { setBusy(''); }
  };

  return (
    <div className="space-y-4 pt-4" data-testid="eot-health-tab">
      <div className="rounded-lg border border-border p-3 space-y-3">
        <p className="text-sm text-foreground font-medium">Bring the programmes in</p>
        <div className="grid md:grid-cols-[2fr_1fr_1fr_auto] gap-2 items-end">
          <div>
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">From this project</p>
            <select value={pick.key} onChange={e => setPick({ ...pick, key: e.target.value })}
                    data-testid="eot-platform-source"
                    className="w-full h-9 rounded-md border border-border bg-background text-foreground text-xs px-2">
              <option value="">Macro, micro, discipline or a saved version…</option>
              {sources.map(s => <option key={s.key} value={s.key}>{s.label}{s.dataDate ? ` · ${String(s.dataDate).slice(0, 10)}` : ''}</option>)}
            </select>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Role</p>
            <select value={pick.role} onChange={e => setPick({ ...pick, role: e.target.value })}
                    data-testid="eot-platform-role"
                    className="w-full h-9 rounded-md border border-border bg-background text-foreground text-xs px-2">
              {ROLES.map(r => <option key={r} value={r}>{r.replace('_', '-').toLowerCase()}</option>)}
            </select>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Data date</p>
            <Input type="date" value={pick.dataDate} onChange={e => setPick({ ...pick, dataDate: e.target.value })}
                   className="h-9 text-xs" data-testid="eot-platform-datadate" />
          </div>
          <Button size="sm" onClick={importPlatform} disabled={!pick.key || busy === 'import'} data-testid="eot-import-platform">
            {busy === 'import' ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1.5" />}
            Import
          </Button>
        </div>
        <div className="grid md:grid-cols-[2fr_1fr_1fr_auto] gap-2 items-end border-t border-border pt-3">
          <div>
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Or import a programme file</p>
            <p className="text-[10px] text-muted-foreground">Primavera P6 (.xer), MS Project (.xml, .mpp), spreadsheet (.xlsx, .csv) or a programme PDF.</p>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Role</p>
            <select value={upRole} onChange={e => setUpRole(e.target.value)} data-testid="eot-upload-role"
                    className="w-full h-9 rounded-md border border-border bg-background text-foreground text-xs px-2">
              {ROLES.map(r => <option key={r} value={r}>{r.replace('_', '-').toLowerCase()}</option>)}
            </select>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Data date</p>
            <Input type="date" value={upDate} onChange={e => setUpDate(e.target.value)} className="h-9 text-xs"
                   data-testid="eot-upload-datadate" />
          </div>
          <>
            <input ref={fileRef} type="file" className="hidden" data-testid="eot-programme-file"
                   accept=".xer,.xml,.mpp,.xlsx,.xlsm,.csv,.pdf"
                   onChange={e => uploadProgramme(e.target.files?.[0])} />
            <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={busy === 'upload'}
                    data-testid="eot-upload-programme">
              {busy === 'upload' ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1.5" />}
              Upload
            </Button>
          </>
        </div>
      </div>

      {recommendation?.method ? (
        <div className="rounded-lg border border-[#6B5CE7]/40 bg-[#6B5CE7]/5 p-3" data-testid="eot-health-recommendation">
          <p className="text-xs text-foreground font-medium flex items-center gap-2">
            <Activity className="w-3.5 h-3.5 text-[#6B5CE7]" />
            These records support {recommendation.method.replaceAll('_', ' ').toLowerCase()}
            {recommendation.alternatives?.length ? ` (or ${recommendation.alternatives.join(', ').replaceAll('_', ' ').toLowerCase()})` : ''}
          </p>
          <p className="text-[11px] text-muted-foreground mt-1">{recommendation.reason}</p>
        </div>
      ) : recommendation ? (
        <p className="text-[11px] text-muted-foreground" data-testid="eot-health-recommendation">{recommendation.reason}</p>
      ) : null}

      {programmes.length === 0 ? (
        <p className="text-xs text-muted-foreground" data-testid="eot-no-programmes">
          No programme is attached yet — bring in the baseline, the updates and the as-built above.
        </p>
      ) : (
        <div className="space-y-2" data-testid="eot-programme-list">
          {programmes.map(p => (
            <div key={p.id} className="rounded-lg border border-border" data-testid={`eot-programme-${p.id}`}>
              <div className="p-3 flex flex-wrap items-center gap-3">
                <button onClick={() => setExpanded(expanded === p.id ? '' : p.id)} className="text-muted-foreground"
                        data-testid={`eot-programme-expand-${p.id}`}>
                  {expanded === p.id ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                </button>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-foreground truncate" title={p.label}>{p.label}</p>
                  <p className="text-[10px] text-muted-foreground">
                    {p.activityCount} activities · {p.linkCount} links · completes {p.projectFinish || '—'}
                    {p.fidelity === 'DATES_ONLY' ? ' · dates only, no logic' : ''}
                    {p.openLoops ? ` · ${p.openLoops} looped link(s) dropped` : ''}
                  </p>
                </div>
                <select value={p.role} onChange={e => patch(p.id, { role: e.target.value })}
                        data-testid={`eot-programme-role-${p.id}`}
                        className="h-8 rounded-md border border-border bg-background text-foreground text-[11px] px-2">
                  {ROLES.map(r => <option key={r} value={r}>{r.replace('_', '-').toLowerCase()}</option>)}
                </select>
                <Input type="date" defaultValue={p.dataDate || ''} key={`${p.id}-${p.dataDate}`}
                       onBlur={e => e.target.value !== p.dataDate && patch(p.id, { dataDate: e.target.value })}
                       className="h-8 w-36 text-[11px]" data-testid={`eot-programme-datadate-${p.id}`} />
                <Gate value={p.health?.gate} testId={`eot-programme-gate-${p.id}`} />
                <Button size="sm" variant="outline" onClick={() => runHealth(p.id)} disabled={busy === `health-${p.id}`}
                        data-testid={`eot-run-health-${p.id}`}>
                  {busy === `health-${p.id}` ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5 mr-1.5" />}
                  {p.health ? 'Re-check' : 'Run 14 checks'}
                </Button>
                <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => remove(p.id)}
                        data-testid={`eot-programme-delete-${p.id}`}><Trash2 className="w-3 h-3" /></Button>
              </div>

              {expanded === p.id && (
                <div className="border-t border-border p-3 space-y-2">
                  {p.note && <p className="text-[10px] text-muted-foreground">{p.note}</p>}
                  {!p.health ? (
                    <p className="text-xs text-muted-foreground">Not assessed yet — run the 14 checks.</p>
                  ) : (
                    <>
                      <p className="text-[11px] text-muted-foreground">
                        {p.health.passed} of 14 passed · {p.health.incompleteCount} incomplete activities
                        {p.health.cpli != null ? ` · CPLI ${p.health.cpli}` : ''}{p.health.bei != null ? ` · BEI ${p.health.bei}` : ''}
                      </p>
                      <table className="w-full text-xs" data-testid={`eot-health-table-${p.id}`}>
                        <thead className="text-muted-foreground uppercase text-[10px]">
                          <tr>{['Check', 'Target', 'Measured', 'Result', 'What it means'].map(h =>
                            <th key={h} className="text-left px-2 py-1">{h}</th>)}</tr>
                        </thead>
                        <tbody>
                          {p.health.checks.map(c => (
                            <tr key={c.key} className="border-t border-border align-top" data-testid={`eot-health-check-${c.key}`}>
                              <td className="px-2 py-1.5 text-foreground">{c.name}</td>
                              <td className="px-2 py-1.5 text-muted-foreground text-[11px]">{c.target}</td>
                              <td className="px-2 py-1.5 text-foreground">{c.value}</td>
                              <td className="px-2 py-1.5"><Gate value={c.status} /></td>
                              <td className="px-2 py-1.5 text-[11px] text-muted-foreground">{c.detail}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {p.health.openEnds?.length > 0 && (
                        <p className="text-[10px] text-muted-foreground">
                          Open ends include: {p.health.openEnds.slice(0, 6).map(o => o.name).join(', ')}
                          {p.health.openEnds.length > 6 ? ` and ${p.health.openEnds.length - 6} more` : ''}
                        </p>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {gate && gate !== 'PASS' && (
        <div className="rounded-lg border border-border p-3 space-y-2" data-testid="eot-health-gate">
          <p className="text-xs text-foreground font-medium flex items-center gap-2">
            <ShieldAlert className="w-3.5 h-3.5 text-amber-500" />
            Overall gate: <Gate value={gate} /> — an analysis can still be run, but the weakness is recorded with it.
          </p>
          {ack.done ? (
            <p className="text-[11px] text-muted-foreground" data-testid="eot-health-ack-note">
              Acknowledged by {ack.by}{ack.note ? ` — “${ack.note}”` : ''}
            </p>
          ) : (
            <>
              <Textarea rows={2} value={ackNote} onChange={e => setAckNote(e.target.value)}
                        placeholder="Why the analysis proceeds despite the programme quality…"
                        data-testid="eot-health-ack-input" />
              <Button size="sm" onClick={acknowledge} disabled={busy === 'ack'} data-testid="eot-health-ack-btn">
                Acknowledge and proceed
              </Button>
            </>
          )}
        </div>
      )}

      <p className="text-[10px] text-muted-foreground flex items-center gap-1">
        <Badge variant="outline" className="text-[9px]">deterministic</Badge>
        Dates, float and the critical path are computed by the scheduling engine from the imported network — no model is involved.
      </p>
    </div>
  );
}
